import { graphql } from "babel-plugin-relay/macro";
import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFragment, useMutation, useQueryLoader } from "react-relay";
import { RightFrameMultipleChoiceElementPart_ContentSubmissionFragment$key } from "@generated/RightFrameMultipleChoiceElementPart_ContentSubmissionFragment.graphql";
import { RightFrameMultipleChoiceElementPart_SubmitMultipleChoiceElementMutation } from "@generated/RightFrameMultipleChoiceElementPart_SubmitMultipleChoiceElementMutation.graphql";
import { RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery } from "@generated/RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery.graphql";
import { ArrowIcon } from "./RightFrameSvgIcons";
import { MultipleChoiceState } from "../../../infecto-lms-webapp/components/content-element/multiplechoice/MultipleChoiceElementDisplay.component";
import { NodeSubmitButton } from "../../../infecto-lms-webapp/components/node/NodeSubmitButton";
import {
	resetLastAnswer,
	setLastAnswer,
} from "../../../infecto-lms-webapp/redux/slices/last-answer.slice";
import { updateProgress } from "../../../infecto-lms-webapp/redux/slices/progress-bar.slice";
import { ProjectContentElementContainer } from "../../../infecto-lms-webapp-impl/containers/ProjectContentElement.container";
import { ShowEditableUserAnswersSummaryDialog } from "../learning/ShowEditableUserAnswersSummaryDialog";
import {WissenwirktMultipleChoiceElementDisplay} from "../content-element/wissenwirkt-multiplechoice-element-display";

const CONTENT_SUBMISSION_FRAGMENT = graphql`
	fragment RightFrameMultipleChoiceElementPart_ContentSubmissionFragment on ContentSubmission {
		id
		learnOpportunity {
			id
			typeDefinition {
				definitionType
				... on LearnOpportunityELearningContentTypeDefinition {
					extension {
						... on LearnContentExtensionImpl {
							configConsequences {
								percentageRequiredToPass
								isBlockedPermanentlyAfterNumFailedTries
								isBlockedTemporarilyForNumDaysAfterOneFailedTry
							}
						}
					}
				}
			}
		}
		definition {
			... on ActiveELearningContentSubmissionDefinition {
				progress {
					percentage
					... on ActiveELearningContentSubmissionProgress {
						isAtLastElement
						numElements
						currentElementIndex
					}
				}

				currentElementState {
					kind

					... on UntouchedElementState {
						element {
							id
							... on MultipleChoiceLearnElement {
								question
								answerOptions {
									answer
								}
								...wissenwirktMultiplechoiceElementDisplay_MultipleChoiceElementV2Fragment
							}
						}
					}

					... on MultipleChoiceInputElementState {
						element {
							id
							... on MultipleChoiceLearnElement {
								answerOptions {
									answer
								}
								answeringType
								...wissenwirktMultiplechoiceElementDisplay_MultipleChoiceElementV2Fragment
							}
						}

						checkedAnswerIndices
					}
				}
			}
		}
		...ProjectContentElementContainer_ContentSubmissionFragment
	}
`;

export const SUBMIT_MUTATION = graphql`
	mutation RightFrameMultipleChoiceElementPart_SubmitMultipleChoiceElementMutation(
		$input: SubmitMultipleChoiceElementV2Input!
	) {
		LearnV2 {
			submitMultipleChoiceElement(input: $input) {
				contentSubmission {
					definition {
						... on ActiveELearningContentSubmissionDefinition {
							progress {
								percentage
							}
						}
					}
					id
					...RightFrameContentSubmissionComponent_ContentSubmissionFragment
				}
			}
		}
	}
`;

export const SUBMITTED_ANSWERS_QUERY = graphql`
	query RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery($submissionId: ID!) {
		LearnV2 {
			GetSubmittedAnswers(submissionId: $submissionId) {
				elementId
				question
				answers
				answerIndices
			}
		}
	}
`;

interface OwnProps {
	contentSubmissionFragmentRef: RightFrameMultipleChoiceElementPart_ContentSubmissionFragment$key;
}

export function RightFrameMultipleChoiceElementPart({ contentSubmissionFragmentRef }: OwnProps) {
	const contentSubmission =
		useFragment<RightFrameMultipleChoiceElementPart_ContentSubmissionFragment$key>(
			CONTENT_SUBMISSION_FRAGMENT,
			contentSubmissionFragmentRef,
		);

	const [submitElement, isSubmitting] =
		useMutation<RightFrameMultipleChoiceElementPart_SubmitMultipleChoiceElementMutation>(
			SUBMIT_MUTATION,
		);

	const [state, setState] = useState<MultipleChoiceState>({
		checkedAnswerIndices: [
			...(contentSubmission.definition.currentElementState?.checkedAnswerIndices || []),
		],
	});
	const reduxLastAnswer = useSelector((state: any) => state.lastAnswer.checkedAnswerIndices);

	const currentCheckedAnswers = contentSubmission.definition.progress?.isAtLastElement
		? reduxLastAnswer
		: state.checkedAnswerIndices;

	useEffect(() => {
		setState({
			checkedAnswerIndices: [
				...(contentSubmission.definition.currentElementState?.checkedAnswerIndices || []),
			],
		});
	}, [
		contentSubmission.definition.currentElementState?.element?.id,
		contentSubmission.definition.currentElementState,
	]);

	if (!contentSubmission.definition.currentElementState?.element?.id) return null;

	const [submittedAnswersQueryRef, loadSubmittedAnswersQuery] =
		useQueryLoader<RightFrameMultipleChoiceElementPart_SubmittedAnswersQuery>(
			SUBMITTED_ANSWERS_QUERY,
		);

	const [isUserAnswersSummaryVisible, setIsUserAnswersSummaryVisible] = useState(false);

	const onHideDialog = (): void => {
		setIsUserAnswersSummaryVisible(false);
	};
	const dispatch = useDispatch();
	const submitLearnElement = () => {
		submitElement({
			variables: {
				input: {
					multipleChoiceElementId: contentSubmission.definition.currentElementState!.element
						?.id as string,
					contentSubmissionId: contentSubmission.id!,
					checkedAnswerIndices: currentCheckedAnswers,
				},
			},
			onCompleted(response) {
				dispatch(
					updateProgress(
						response.LearnV2.submitMultipleChoiceElement?.contentSubmission.definition.progress
							?.percentage,
					),
				);
				dispatch(resetLastAnswer());
			},
		});
	};

	const isDisabled = contentSubmission.definition.progress?.isAtLastElement
		? reduxLastAnswer.length == 0
		: state.checkedAnswerIndices.length === 0;
	return (
		<ProjectContentElementContainer
			contentSubmissionFragmentRef={contentSubmission}
			nextButton={
				<NodeSubmitButton
					disabled={isSubmitting || isDisabled}
					onClick={() => {
						if (contentSubmission.definition.progress?.isAtLastElement) {
							loadSubmittedAnswersQuery(
								{
									submissionId: contentSubmission.id,
								},
								{ fetchPolicy: "network-only" },
							);
							setIsUserAnswersSummaryVisible(true);
						} else {
							submitLearnElement();
						}
					}}
				>
					<span className="mr-1 text-xs md:text-base">
						{contentSubmission.definition.progress?.isAtLastElement ? "ZUSAMMENFASSUNG" : "WEITER"}
					</span>
					<ArrowIcon />
				</NodeSubmitButton>
			}
			footer={
				<div className="flex flex-column lg:flex-row mt-5 px-5 align-items-center justify-content-between">
					{contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
						?.isBlockedTemporarilyForNumDaysAfterOneFailedTry && (
						<div>
							{`Bei Nichtbestehen: Blockierung für ${
								contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
									?.isBlockedTemporarilyForNumDaysAfterOneFailedTry
							} Tage`}
						</div>
					)}
					{contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
						?.isBlockedPermanentlyAfterNumFailedTries && (
						<div>{`Bei ${
							contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
								?.isBlockedPermanentlyAfterNumFailedTries
						} Fehlversuchen: Dauerhafte Sperrung`}</div>
					)}
				</div>
			}
		>
			<div className="flex mt-4 lg:mt-0 px-5 justify-content-between align-items-center">
				<span className="text-base font-bold">
					{/*eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain*/}
					Frage {contentSubmission?.definition.progress?.currentElementIndex! + 1} von{" "}
					{/*eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain*/}
					{contentSubmission?.definition.progress?.numElements!}{" "}
				</span>
				{contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
					?.percentageRequiredToPass && (
					<span className="text-base">
						Bestehensquote:{" "}
						{
							contentSubmission.learnOpportunity?.typeDefinition.extension?.configConsequences
								?.percentageRequiredToPass
						}
						%
					</span>
				)}
			</div>
			<Suspense fallback="Lade...">
				{submittedAnswersQueryRef! && contentSubmission.definition.progress?.isAtLastElement ? (
					<ShowEditableUserAnswersSummaryDialog
						isVisible={isUserAnswersSummaryVisible}
						onHideDialog={onHideDialog}
						queryRef={submittedAnswersQueryRef!}
						submitLearnElement={submitLearnElement}
						totalElementsCount={contentSubmission?.definition.progress?.numElements!} //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
						currentElement={contentSubmission.definition.currentElementState.element}
						checkedAnswerIndices={currentCheckedAnswers}
						setCheckedAnswerIndices={(indices) => {
							if (contentSubmission.definition.progress?.isAtLastElement) {
								dispatch(setLastAnswer(indices));
							} else {
								setState({ checkedAnswerIndices: indices });
							}
						}}
						contentSubmissionId={contentSubmission.id}
						reloadSubmittedAnswersQuery={() =>
							loadSubmittedAnswersQuery(
								{ submissionId: contentSubmission.id },
								{ fetchPolicy: "network-only" },
							)
						}
					/>
				) : null}
			</Suspense>

			<WissenwirktMultipleChoiceElementDisplay
				className={"w-full text-center"}
				checkedAnswerIndices={currentCheckedAnswers}
				setCheckedAnswerIndices={(indices) => {
					if (contentSubmission.definition.progress?.isAtLastElement) {
						dispatch(setLastAnswer(indices));
					} else {
						setState({ checkedAnswerIndices: indices });
					}
				}}
				multipleChoiceElementFragmentRef={contentSubmission.definition.currentElementState.element!}
				currentElementIndex={contentSubmission?.definition.progress?.currentElementIndex!} //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
			/>
		</ProjectContentElementContainer>
	);
}
